import type { AuditLogResponse } from '@core/api/auditLog';
import { createCrudApi } from '@core/api/createCrudApi';
import type { BaseEntity } from '@core/interfaces';
import axiosInstance from '@core/utils/axiosInstance';
import type { ChasubleBodyType } from '../src/enum/chasubleBodyType';
import type { ChasubleGothicType } from '../src/enum/chasubleGothicType';
import type { ChasublePatchingType } from '../src/enum/chasublePatchingType';
import { OrderStatus } from '../src/enum/orderStatus';
import { ReleaseType } from '../src/enum/releaseType';
import { SizeMode } from '../src/enum/sizeMode';
import type { Collar } from './collar';
import { Color } from './color';
import { Customer } from './customer';
import type { Design } from './design';
import { Fabric } from './fabric';
import { LegacyCollar } from './legacyCollar';
import type { MaterialColor } from './materialColor';
import { Size } from './size';
import type { StoleCut } from './stoleCut';
import { Vestment } from './vestment';

export interface Order extends BaseEntity {
  customer: Customer;
  dueDate?: string | null;
  items: OrderItem[];
  itemSummary: string;
  notes?: string | null;
  status: OrderStatus;
  releaseType?: ReleaseType | null;
  releasedAt?: string | null;
}

export interface OrderItem extends BaseEntity {
  vestment: Vestment;
  design?: Design | null;
  quantity?: number | null;
  size?: Size | null;
  forCustomer?: Customer | null;
  notes?: string | null;
  productionStage?: string | null;
  productionProgress?: number | null;
  sizeMode?: SizeMode | null;
  printQueuedAt?: string | null;
  missingRequiredFields?: Record<string, string>;
}

export interface ChasubleItem extends OrderItem {
  length?: number | null;
  sleeve?: number | null;
  neck?: number | null;
  bodyFabric?: Fabric | null;
  bodyColor?: Color | null;
  bodyType?: ChasubleBodyType | null;
  panelFabric?: Fabric | null;
  panelColor?: Color | null;
  centerFinish?: number | null;
  panelWidth?: number | null;
  panelLength?: number | null;
  patchingType?: ChasublePatchingType | null;
  thread?: MaterialColor | null;
  trimming?: MaterialColor | null;
  gothicType?: ChasubleGothicType | null;
  panelDimension?: string | null;
  galoon?: MaterialColor | null;
  liningFabric?: Fabric | null;
  liningColor?: Color | null;
  stoleSize?: string | null;
  collar?: Collar | null;
  legacyCollar?: LegacyCollar | null;
  collarFabric?: Fabric | null;
  collarColor?: Color | null;
}

export interface AlbItem extends OrderItem {
  subtype?: string | null;
  length?: number | null;
  shoulder?: number | null;
  sleeve?: number | null;
  neck?: number | null;
  chest?: number | null;
  waist?: number | null;
  figure?: number | null;
  bodyFabric?: Fabric | null;
  bodyColor?: Color | null;
  thread?: MaterialColor | null;
  laceStyle?: string | null;
  laceOption?: string | null;
  withLace?: boolean | null;
  collar?: LegacyCollar | null;
}

export interface StoleItem extends OrderItem {
  length?: number | null;
  stoleCut?: StoleCut | null;
  panelFabric?: Fabric | null;
  panelColor?: Color | null;
  cord?: MaterialColor | null;
  tassel?: MaterialColor | null;
  patchingFabric?: Fabric | null;
  patchingColor?: Color | null;
}

export interface MitreItem extends OrderItem {
  head?: number | null;
  bodyFabric?: Fabric | null;
  bodyColor?: Color | null;
  panelFabric?: Fabric | null;
  panelColor?: Color | null;
  liningFabric?: Fabric | null;
  liningColor?: Color | null;
  tassel?: MaterialColor | null;
}

export interface CreateOrderData {
  customer: number;
  dueDate?: string | null;
  items: (
    | CreateChasubleItemData
    | CreateAlbItemData
    | CreateStoleItemData
    | CreateMitreItemData
  )[];
  notes?: string | null;
}

interface CreateOrderItemData {
  id?: number;
  vestment: number;
  design?: number | string | null;
  quantity?: number | null;
  size?: number | null;
  forCustomer?: number | null;
  notes?: string | null;
}

export interface CreateChasubleItemData extends CreateOrderItemData {
  length?: number | null;
  sleeve?: number | null;
  neck?: number | null;
  bodyFabric?: number | null;
  bodyColor?: number | null;
  bodyType?: string | null;
  panelFabric?: number | null;
  panelColor?: number | null;
  centerFinish?: number | null;
  panelWidth?: number | null;
  panelLength?: number | null;
  patchingType?: string | null;
  thread?: number | null;
  trimming?: number | null;
  gothicType?: string | null;
  panelDimension?: string | null;
  galoon?: number | null;
  liningFabric?: number | null;
  liningColor?: number | null;
  stoleSize?: string | null;
  collar?: number | null;
  legacyCollar?: number | null;
  collarFabric?: number | null;
  collarColor?: number | null;
}

export interface CreateAlbItemData extends CreateOrderItemData {
  subtype?: string | null;
  length?: number | null;
  shoulder?: number | null;
  sleeve?: number | null;
  neck?: number | null;
  chest?: number | null;
  waist?: number | null;
  figure?: number | null;
  bodyFabric?: number | null;
  bodyColor?: number | null;
  thread?: number | null;
  laceStyle?: string | null;
  laceOption?: string | null;
  withLace?: boolean | null;
  collar?: number | null;
}

export interface CreateStoleItemData extends CreateOrderItemData {
  length?: number | null;
  stoleCut?: number | null;
  panelFabric?: number | null;
  panelColor?: number | null;
  cord?: number | null;
  tassel?: number | null;
  patchingFabric?: number | null;
  patchingColor?: number | null;
}

export interface CreateMitreItemData extends CreateOrderItemData {
  head?: number | null;
  bodyFabric?: number | null;
  bodyColor?: number | null;
  panelFabric?: number | null;
  panelColor?: number | null;
  liningFabric?: number | null;
  liningColor?: number | null;
  tassel?: number | null;
}

export type UpdateOrderData = Partial<CreateOrderData>;

export interface PatchOrderDetailsData {
  customer: number;
  dueDate?: string | null;
  notes?: string | null;
}

const orderApi = createCrudApi<Order, CreateOrderData, UpdateOrderData>(
  'order'
);

export const getAllOrders = orderApi.getAll;
export const getOrderById = orderApi.getById;
export const createOrder = orderApi.create;
export const updateOrder = orderApi.update;
export const deleteOrder = orderApi.delete;

export interface OrderQuery {
  search?: string;
  status?: string;
  category?: string;
  sort?: string;
  direction?: 'asc' | 'desc';
}

export interface OrderPageParams extends OrderQuery {
  offset: number;
  limit: number;
}

export interface OrderPage {
  items: Order[];
  total: number;
  offset: number;
  limit: number;
}

export async function getOrdersPage(
  params: OrderPageParams
): Promise<OrderPage> {
  const { data } = await axiosInstance.get<OrderPage>('/order/page', {
    params,
  });
  return data;
}

export const patchOrderDetails = async (
  id: number,
  data: PatchOrderDetailsData
): Promise<Order> => {
  const { data: order } = await axiosInstance.patch<Order>(
    `/order/${id}`,
    data
  );
  return order;
};

export const moveOrderToProduction = async (id: number): Promise<Order> => {
  const { data } = await axiosInstance.post<Order>(
    `/order/${id}/start-production`
  );
  return data;
};

export const releaseOrder = async (
  id: number,
  releaseType: ReleaseType
): Promise<Order> => {
  const { data } = await axiosInstance.put<Order>(`/order/${id}/release`, {
    releaseType,
  });
  return data;
};

export const undoReleaseOrder = async (id: number): Promise<Order> => {
  const { data } = await axiosInstance.post<Order>(`/order/${id}/undo-release`);
  return data;
};

export const cancelOrder = async (id: number): Promise<Order> => {
  const { data } = await axiosInstance.post<Order>(`/order/${id}/cancel`);
  return data;
};

export const undoCancelOrder = async (id: number): Promise<Order> => {
  const { data } = await axiosInstance.post<Order>(`/order/${id}/undo-cancel`);
  return data;
};

export const confirmOrderReprint = async (
  orderId: number,
  itemIds: number[]
): Promise<void> => {
  await axiosInstance.post(`/order/${orderId}/confirm-reprint`, { itemIds });
};

export async function getOrderAuditLogs(
  orderId: number,
  query: { page?: number; limit?: number } = {}
): Promise<AuditLogResponse> {
  const params = new URLSearchParams();
  if (query.page != null) params.set('page', String(query.page));
  if (query.limit != null) params.set('limit', String(query.limit));
  const qs = params.toString();
  const url = qs
    ? `/order/${orderId}/audit-log?${qs}`
    : `/order/${orderId}/audit-log`;
  const { data } = await axiosInstance.get<AuditLogResponse>(url);
  return data;
}

export type OrderItemPayload = CreateOrderData['items'][number];

export const createOrderItem = async (
  orderId: number,
  payload: OrderItemPayload
): Promise<Order> => {
  const { data } = await axiosInstance.post<Order>(
    `/order/${orderId}/item`,
    payload
  );
  return data;
};

export const duplicateOrderItem = async (
  orderId: number,
  itemId: number
): Promise<Order> => {
  const { data } = await axiosInstance.post<Order>(
    `/order/${orderId}/item/${itemId}/duplicate`
  );
  return data;
};

export const updateOrderItem = async (
  orderId: number,
  itemId: number,
  payload: OrderItemPayload
): Promise<Order> => {
  const { data } = await axiosInstance.put<Order>(
    `/order/${orderId}/item/${itemId}`,
    payload
  );
  return data;
};

export const deleteOrderItem = async (
  orderId: number,
  itemId: number
): Promise<Order> => {
  const { data } = await axiosInstance.delete<Order>(
    `/order/${orderId}/item/${itemId}`
  );
  return data;
};
